import React, { createContext, useState, useEffect, useCallback } from 'react';
import db, { initDB, formatChapterId, parseChapterId, ProgressStatus } from '../db';
import { writeChapterHash } from '../utils/chapterHash';

export const AudioContext = createContext();

export const AudioProvider = ({ children }) => {
  const [currentChapter, setCurrentChapter] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentSession, setCurrentSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [chapterProgress, setChapterProgress] = useState({});

  useEffect(() => {
    const init = async () => {
      await initDB();
      const sessionList = await db.sessions.orderBy('last_listened_at').reverse().limit(10).toArray();
      setSessions(sessionList);
      const progress = await db.chapterProgress.toArray();
      setChapterProgress(
        progress.reduce((acc, p) => ({ 
          ...acc, 
          [p.chapter_id]: { status: p.status, progress_percent: p.progress_percent }
        }), {})
      );
    };
    init();
  }, []);

  useEffect(() => {
    writeChapterHash(currentChapter);
  }, [currentChapter]);

  const isConsecutive = (newChapter, prevChapter) => {
    const newId = formatChapterId(newChapter.bookId, newChapter.chapter);
    
    // Check if prevChapter is an object or already a string ID
    let prevId;
    if (typeof prevChapter === 'string') {
      prevId = prevChapter;
    } else if (prevChapter && typeof prevChapter === 'object') {
       prevId = formatChapterId(prevChapter.bookId, prevChapter.chapter);
    } else {
       return false;
    }
    
    // Parse to check numerical continuity
    const { bookId, chapter } = parseChapterId(prevId);
    return newId === formatChapterId(bookId, chapter + 1);
  };

  const updateSession = async (newChapter) => {
    const chapterId = formatChapterId(newChapter.bookId, newChapter.chapter);
    const progress = chapterProgress[chapterId];
    const book = await db.books.get(newChapter.bookId);
    
    // If near end of current chapter and it's the last chapter, set end chapter to next book's first chapter
    if (progress?.progress_percent >= 95 && newChapter.chapter === book.chapters) {
      const nextBook = await db.books.where('id').above(book.id).first();
      if (nextBook) {
        const nextChapterId = formatChapterId(nextBook.id, 1);
        if (currentSession) {
          const updatedSession = { 
            ...currentSession, 
            endChapter: nextChapterId,
            last_listened_at: new Date().toISOString(),
            dirty: 1
          };
          setCurrentSession(updatedSession);
          await db.sessions.update(currentSession.id, updatedSession);
        } else {
          const newSession = {
            startChapter: chapterId,
            endChapter: nextChapterId,
            last_listened_at: new Date().toISOString(),
            dirty: 1
          };
          const id = await db.sessions.add(newSession);
          setCurrentSession({ ...newSession, id });
        }
      }
    } else if (currentSession && (isConsecutive(newChapter, currentSession.endChapter) || progress?.progress_percent >= 95)) {
      // Update existing session for consecutive chapters or near-end chapters
      const updatedSession = { 
        ...currentSession, 
        endChapter: chapterId,
        last_listened_at: new Date().toISOString(),
        dirty: 1
      };
      setCurrentSession(updatedSession);
      await db.sessions.update(currentSession.id, updatedSession);
    } else {
      // Create new session
      if (currentSession) {
        await db.sessions.put({ 
          ...currentSession, 
          last_listened_at: new Date().toISOString(),
          dirty: 1 
        });
      }
      const newSession = {
        startChapter: chapterId,
        endChapter: chapterId,
        last_listened_at: new Date().toISOString(),
        dirty: 1
      };
      const id = await db.sessions.add(newSession);
      setCurrentSession({ ...newSession, id });
    }

    // Update sessions list
    const sessionList = await db.sessions.orderBy('last_listened_at').reverse().limit(10).toArray();
    setSessions(sessionList);
  };

  return (
    <AudioContext.Provider
      value={{
        currentChapter,
        setCurrentChapter,
        isPlaying,
        setIsPlaying,
        currentTime,
        setCurrentTime,
        duration,
        setDuration,
        currentSession,
        updateSession,
        sessions,
        setSessions,
        chapterProgress,
        setChapterProgress,
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};